import { DragDropModule } from '@angular/cdk/drag-drop'
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router } from '@angular/router'
import { filter, map, startWith } from 'rxjs'
import {
  CallEndReason,
  WhatsappCallService,
} from 'src/app/services/whatsapp-call.service'

@Component({
  selector: 'ht-whatsapp-call-widget',
  imports: [DragDropModule],
  templateUrl: './whatsapp-call-widget.component.html',
  styleUrls: ['./whatsapp-call-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WhatsappCallWidgetComponent {
  readonly #callService = inject(WhatsappCallService)
  readonly #router = inject(Router)

  readonly isVisible = computed(() => this.#callService.isCalling())
  readonly isLive = computed(() => this.#callService.isCallConnected())
  readonly isRingingCall = computed(() => this.#callService.isRingingCall())
  readonly contactName = computed(() => this.#callService.contactName())
  readonly isMuted = computed(() => this.#callService.isMuted())
  readonly isEnding = computed(() => this.#callService.isEnding())
  readonly hangupDeactivated = computed(() =>
    this.#callService.hangupDeactivated()
  )

  private readonly callCaseId = computed(() => this.#callService.callCaseId())
  private readonly currentUrl = toSignal(
    this.#router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(() => this.#router.url),
      startWith(this.#router.url)
    ),
    { initialValue: this.#router.url }
  )

  // The case is considered "open" only when the user is on its inbox route.
  // Otherwise we offer a link to navigate to it.
  private readonly isCaseOpen = computed(() => {
    const caseId = this.callCaseId()
    if (!caseId) {
      return false
    }
    return this.currentUrl().includes(`/desk/inbox/${caseId}`)
  })
  readonly isCaseLinkVisible = computed(
    () => !!this.callCaseId() && !this.isCaseOpen()
  )
  readonly endLabel = computed(() => {
    switch (this.#callService.callEndReason()) {
      case CallEndReason.DECLINED:
        return 'Call declined'
      case CallEndReason.FAILED:
        return 'Call failed'
      case CallEndReason.NOT_ANSWERED:
        return 'Call not answered'
      case CallEndReason.ENDED_BY_CUSTOMER:
        return 'Call ended by customer'
      case CallEndReason.ENDED_BY_AGENT:
        return 'Call ended'
      default:
        return ''
    }
  })

  private readonly elapsedSeconds = signal(0)
  readonly elapsedLabel = computed(() =>
    this.formatElapsed(this.elapsedSeconds())
  )

  private readonly callingAudio = new Audio('assets/sounds/ringback.mp3')

  constructor() {
    effect(onCleanup => {
      if (!this.isLive()) {
        this.elapsedSeconds.set(0)
        return
      }
      const intervalId = setInterval(
        () => this.elapsedSeconds.update(seconds => seconds + 1),
        1000
      )
      onCleanup(() => clearInterval(intervalId))
    })

    this.callingAudio.loop = true
    effect(() => {
      if (this.#callService.isRingingCall()) {
        this.callingAudio.play().catch(() => {})
      } else {
        this.callingAudio.pause()
        this.callingAudio.currentTime = 0
      }
    })
  }

  onToggleMute() {
    this.#callService.toggleMute()
  }

  onHangUp() {
    this.#callService.hangUp().subscribe({
      error: err => console.error('Could not hang up WhatsApp call:', err),
    })
  }

  onNavigateToCase() {
    const caseId = this.callCaseId()
    if (!caseId) {
      return
    }
    this.#router.navigate(['/desk/inbox', caseId])
  }

  private registerAudioEffect(
    condition: () => boolean,
    src: string,
    loop: boolean
  ): void {
    effect(onCleanup => {
      if (!condition()) {
        return
      }
      const audio = new Audio(src)
      audio.loop = loop
      audio.play().catch(() => {})
      onCleanup(() => {
        audio.pause()
        audio.currentTime = 0
      })
    })
  }

  private formatElapsed(totalSeconds: number): string {
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    const pad = (value: number) => value.toString().padStart(2, '0')
    return `${pad(minutes)}:${pad(seconds)}`
  }
}
